import React, { useEffect, useState, useRef } from "react";
import { useForm } from "@mantine/form";
import { PlaceholderUi, validateAddress, validateSiteName, validateLatitude, validateLongitude } from "../../helpers/common";
import { useDispatch, useSelector } from "react-redux";
import { onAddnewSite, onupdateSite } from "../../store/actions";
import { getClientsList } from "../../store/actions";
import MantineButton from "../../components/mantine/button";
import MantineFlex from "../../components/mantine/flex";
import MantineSelect from "../../components/mantine/select";
import MantineTextInput from "../../components/mantine/textinput";


const UserForm = (props: any) => {
    const { handleResponseUserForm, userData, modalclose, dispatch, currentPrimaryColor, clientNames, sitenameNames } = props;
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const addressRef = useRef<HTMLInputElement | null>(null);
    const initialValues = {
        siteName: '',
        address: '',
        latitude: '',
        longitude: '',
        clientId: ''
    };
    const form = useForm({
        initialValues,
        validate: {
            siteName: (val) => validateSiteName(val),
            address: (val) => validateAddress(val),
            latitude: validateLatitude,
            longitude: validateLongitude,
            clientId: (val) => (val ? null : "Please select a client"),
        },
    });

    useEffect(() => {
        const loadGoogleMaps = () => {
            if (!document.querySelector(`script[src*="maps.googleapis.com"]`)) {
                const script = document.createElement("script");
                script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
                script.async = true;
                script.defer = true;
                script.onload = () => Autocomplete();
                document.head.appendChild(script);
            }
        };  
        loadGoogleMaps();
    }, []);
    const Autocomplete = () => {
        const input = addressRef.current;
        if (!input) return;

        const autocomplete = new window.google.maps.places.Autocomplete(input);

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            if (place && place.geometry && place.geometry.location) {
                form.setFieldValue("latitude", place.geometry.location.lat().toString());
                form.setFieldValue("longitude", place.geometry.location.lng().toString());
                form.setFieldValue("address", place.formatted_address || "Address not available"); 
                setShowSuggestions(false); 
            }
        });
    };
    const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const address = event.target.value;
        form.setFieldValue("address", address);

        if (address.length > 2) {
            setShowSuggestions(true);
            const service = new window.google.maps.places.AutocompleteService();
            service.getPlacePredictions({ input: address }, (predictions: any, status: any) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    setSuggestions(predictions);
                } else {
                    setSuggestions([]);
                }
            });
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionClick = (suggestion: any) => {
        const selectedAddress = suggestion.description;

        form.setFieldValue("address", selectedAddress);

        const service = new window.google.maps.places.PlacesService(document.createElement('div'));
        service.getDetails({ placeId: suggestion.place_id }, (place: any, status: any) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
                form.setFieldValue("latitude", place.geometry.location.lat().toString());
                form.setFieldValue("longitude", place.geometry.location.lng().toString());
            }
        });
        setSuggestions([]);
        setShowSuggestions(false);
    };
    const userFormData = () => {
        
        if (!form.values.latitude || !form.values.longitude) {
            form.setFieldValue("latitude", "0");  
            form.setFieldValue("longitude", "0"); 
        }
       
        form.validate();
        if (form.isValid()) {        
            const selectedClient = clientNames.find((client: any) => client.value === form.values.clientId);
            const data = {
                ...form.values,
                "id": userData?.siteId,
                "siteName": form.values.siteName,
                "siteaddress": form.values.address,
                "latitude": form.values.latitude ||"",
                "longitude": form.values.longitude ||"",
                clientId: selectedClient.value,
            };
            if (data?.id == undefined) {
                const filteredData = Object.fromEntries(
                    Object.entries(data)?.filter(([_, value]) => value !== undefined && value !== null)
                );
                dispatch(onAddnewSite(filteredData, handleResponseUserForm))
            }
            else {
                dispatch(onupdateSite(data, handleResponseUserForm))
            }
        }
    }
    
    useEffect(() => {
        if (userData) {
            form.setValues({
                siteName: userData?.siteName,
                clientId: userData?.clientId ? String(userData.clientId) : '',
                latitude: userData?.latitude,
                longitude: userData?.longitude,
                address: userData?.address
            });
        }
    }, [userData])

    return (
        <>
            <form onSubmit={form.onSubmit(() => { })}>
                <MantineFlex className='flex-wrap md:flex-nowrap md:gap-4'>
                    <div className="w-full my-2">
                        <MantineTextInput
                            {...form.getInputProps('siteName')}
                            label={<span>Site Name <span style={{ color: 'red' }}>*</span></span>}
                            placeholder="Enter Your Site Name"
                            value={form.values.siteName}
                            onChange={(event: any) => form.setFieldValue('siteName', event.currentTarget.value)}
                            classNames={{ input: `${PlaceholderUi}`, label: "text-sm" }}
                            error={form.errors.siteName}
                        />
                    </div>
                    <div className="w-full my-2" style={{ position: 'relative' }}>
                        <MantineTextInput
                            {...form.getInputProps('address')}
                            label={<span>Address <span style={{ color: 'red' }}>*</span></span>}
                            placeholder="Enter Your SiteAddress"
                            ref={addressRef}
                            onChange={handleAddressChange}
                            onFocus={() => setShowSuggestions(true)}
                            classNames={{ input: `${PlaceholderUi}`, label: "text-sm" }}
                            error={form.errors.address}
                        />
                        
                        {showSuggestions && suggestions.length > 0 && (
                            <ul
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    border: '1px solid #ccc',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    backgroundColor: 'white',
                                    zIndex: 1000
                                }}
                            >
                                {suggestions.map((suggestion: any, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        style={{ cursor: 'pointer', padding: '5px' }}
                                    >
                                        {suggestion.description}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                </MantineFlex>

                <MantineFlex className='flex-wrap md:flex-nowrap md:gap-4'>
                    <div className="w-1/2 my-2 mt-1">

                        <MantineSelect
                            {...form.getInputProps('clientId')}
                            required
                            label="Client Name"
                            placeholder="Select a Client Name"
                            data={clientNames}
                            classNames={{ input: `${PlaceholderUi}`, label: "text-sm" }}
                            error={form.errors.clientId}
                        />
                    </div>

                </MantineFlex>


                <MantineFlex justify='flex-end' className='gap-4'>
                    <MantineButton name={'Save'} className={" mt-[15px] text-sm font-thin !w-[90px]"} onClick={() => userFormData()} color={currentPrimaryColor} />
                    <MantineButton name={'Cancel'} className={"bg-secondary_color hover:bg-hover_secondary_btn mt-[15px] text-sm font-thin !w-[100px]"} onClick={() => modalclose()} />
                </MantineFlex>
            </form>
        </>
    )
}
export default UserForm;

